import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { HiCheck, HiHeart, HiOutlineHeart } from "react-icons/hi";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/productdetails.scss";

const apparelSizeGuide = {
  XS: { chest: "33–35", waist: "27–29" },
  S: { chest: "35–37", waist: "29–31" },
  M: { chest: "38–40", waist: "32–34" },
  L: { chest: "41–43", waist: "35–37" },
  XL: { chest: "44–46", waist: "38–40" },
  XXL: { chest: "47–49", waist: "41–43" },
};

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addCart, toggleWishlist, isWishlisted } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(product?.colors?.[0] || "");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return productData
      .filter((item) => item.category === product.category && item.id !== product.id)
      .slice(0, 3);
  }, [product]);

  useEffect(() => {
    document.title = product
      ? `${product.name} | ${siteConfig.brandName}`
      : `Product Not Found | ${siteConfig.brandName}`;
  }, [product]);

  useEffect(() => {
    setSelectedSize(product?.size?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setAdded(false);
  }, [product]);

  const formatPrice = (price) =>
    new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
      style: "currency",
      currency: siteConfig.currency?.code || "USD",
    }).format(price);

  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found">
        <p>404 / PRODUCT NOT FOUND</p>
        <h1>We couldn’t find this piece.</h1>
        <Link onClick={toTop} to="/shop">BACK TO ALL PRODUCTS</Link>
      </main>
    );
  }

  const soldOut = Number(product.stock) <= 0;
  const wished = isWishlisted(product.id);
  const shippingThreshold = siteConfig.shipping?.freeThreshold || 250;
  const returnWindow = siteConfig.returns?.windowDays || 14;
  const maxQuantity = Math.min(10, Math.max(1, Number(product.stock)));
  const sizeGuideRows = product.size.filter((size) => apparelSizeGuide[size]);

  const handleAddToCart = () => {
    if (soldOut || !selectedSize) return;
    addCart(product.id, { quantity, size: selectedSize, color: selectedColor });
    setAdded(true);
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: product.images,
    description: product.description.join(" "),
    brand: { "@type": "Brand", name: siteConfig.brandName },
    offers: {
      "@type": "Offer",
      priceCurrency: siteConfig.currency?.code || "USD",
      price: product.price,
      availability: soldOut
        ? "https://schema.org/OutOfStock"
        : "https://schema.org/InStock",
    },
  };

  return (
    <main className="product-detail">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <nav className="product-detail__breadcrumbs" aria-label="Breadcrumb">
        <Link to="/" onClick={toTop}>HOME</Link>
        <span>/</span>
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`} onClick={toTop}>
          {product.category.toUpperCase()}
        </Link>
        <span>/</span>
        <span>{product.name}</span>
      </nav>

      <div className="product-detail__layout">
        <section
          className={`product-gallery ${
            product.images.length === 1 ? "product-gallery--single" : ""
          }`}
          aria-label={`${product.name} gallery`}
        >
          {product.images.length > 1 && (
            <div className="product-gallery__thumbs">
              {product.images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={selectedImage === index ? "is-active" : ""}
                  onClick={() => setSelectedImage(index)}
                  aria-label={`Show product image ${index + 1}`}
                >
                  <img src={image} alt="" width="120" height="150" />
                </button>
              ))}
            </div>
          )}
          <div className="product-gallery__main">
            <img
              src={product.images[selectedImage]}
              alt={product.name}
              width="1600"
              height="2000"
            />
          </div>
        </section>

        <aside className="product-detail__info">
          <div className="product-detail__status">
            {product.isNew && <span>NEW ARRIVAL</span>}
            <span>{soldOut ? "SOLD OUT" : "IN STOCK"}</span>
          </div>
          <h1>{product.name}</h1>
          <p className="product-detail__price">{formatPrice(product.price)}</p>

          <div className="product-detail__description">
            {product.description.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>

          <fieldset className="product-option">
            <legend>COLOR <span>{selectedColor}</span></legend>
            <div className="product-option__choices product-option__choices--colors">
              {product.colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={selectedColor === color ? "is-active" : ""}
                  onClick={() => setSelectedColor(color)}
                  aria-pressed={selectedColor === color}
                >
                  {selectedColor === color && <HiCheck aria-hidden="true" />}
                  {color}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="product-option">
            <legend>SIZE <a href="#size-guide">SIZE GUIDE</a></legend>
            <div className="product-option__choices product-option__choices--sizes">
              {product.size.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={selectedSize === size ? "is-active" : ""}
                  onClick={() => setSelectedSize(size)}
                  aria-pressed={selectedSize === size}
                >
                  {size}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="product-detail__purchase">
            <label>
              <span>QUANTITY</span>
              <select value={quantity} onChange={(event) => setQuantity(Number(event.target.value))}>
                {Array.from({ length: maxQuantity }, (_, index) => index + 1).map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </label>
            <button type="button" disabled={soldOut} onClick={handleAddToCart}>
              {soldOut ? "SOLD OUT" : "ADD TO CART"}
            </button>
            <button
              type="button"
              className={`product-detail__wishlist ${wished ? "is-active" : ""}`}
              onClick={() => toggleWishlist(product.id)}
              aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
              aria-pressed={wished}
            >
              {wished ? <HiHeart /> : <HiOutlineHeart />}
            </button>
          </div>

          {added && (
            <div className="product-detail__added" role="status">
              <span><HiCheck /> ADDED TO YOUR CART</span>
              <Link to="/cartpage" onClick={toTop}>VIEW CART</Link>
            </div>
          )}

          <div className="product-detail__accordions">
            <details open>
              <summary>DESCRIPTION &amp; DETAILS</summary>
              <ul>
                {product.Features.map((feature) => <li key={feature}>{feature}</li>)}
              </ul>
              <dl>
                {Object.entries(product.attributes).map(([label, value]) => (
                  <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                ))}
              </dl>
            </details>
            <details>
              <summary>SHIPPING / RETURNS</summary>
              <p>
                Complimentary domestic shipping on orders over {formatPrice(shippingThreshold)}.
                Unworn items may be returned within {returnWindow} days of delivery.
              </p>
              <Link to="/shipping" onClick={toTop}>FULL SHIPPING DETAILS</Link>
            </details>
            <details id="size-guide" open>
              <summary>CARE &amp; SIZING</summary>
              <p>
                Follow the garment label. Store knits folded and spot clean structured
                outerwear between professional cleanings. Measurements below are a general
                body guide in inches; check the product fit notes before choosing.
              </p>
              {sizeGuideRows.length > 0 ? (
                <div className="product-size-guide">
                  <table>
                    <caption className="visually-hidden">
                      General body size guide in inches
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Size</th>
                        <th scope="col">Chest</th>
                        <th scope="col">Waist</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sizeGuideRows.map((size) => (
                        <tr key={size}>
                          <th scope="row">{size}</th>
                          <td>{apparelSizeGuide[size].chest}</td>
                          <td>{apparelSizeGuide[size].waist}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>One-size accessories include an adjustable closure where noted.</p>
              )}
            </details>
          </div>
        </aside>
      </div>

      {relatedProducts.length > 0 && (
        <section className="product-related" aria-labelledby="related-title">
          <p>KEEP LOOKING</p>
          <h2 id="related-title">A few more to explore.</h2>
          <div>
            {relatedProducts.map((related) => (
              <Link key={related.id} to={`/shopDetails/${related.id}`} onClick={toTop}>
                <img
                  src={related.image}
                  alt={related.name}
                  width="1200"
                  height="1500"
                  loading="lazy"
                  decoding="async"
                />
                <h3>{related.name}</h3>
                <span>{formatPrice(related.price)}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
